import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';
import { AttendanceDetailTotals, TotalsByCategoryData } from '../../models/divine-service.model';

export interface AttendanceTotalsDialogData {
  totals: TotalsByCategoryData;
  aggregateVisitsAdults: number;
  aggregateVisitsMinors: number;
  readonly: boolean;
}

@Component({
  selector: 'app-attendance-totals-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    ModalShellComponent
  ],
  templateUrl: './attendance-totals-dialog.component.html',
  styleUrl: './attendance-totals-dialog.component.scss'
})
export class AttendanceTotalsDialogComponent {
  readonly modalConfig: ModalShellConfig = { title: 'Detalle de concurrencia', size: 'lg', layout: 'summary' };
  readonly form: FormGroup<{
    visitsAdults: FormControl<number>;
    visitsMinors: FormControl<number>;
    newAdults: FormControl<number>;
    newMinors: FormControl<number>;
  }>;
  readonly nominalVisitsAdults: number;
  readonly nominalVisitsMinors: number;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<
      AttendanceTotalsDialogComponent,
      AttendanceDetailTotals | null
    >,
    @Inject(MAT_DIALOG_DATA) readonly data: AttendanceTotalsDialogData
  ) {
    this.nominalVisitsAdults =
      Math.max(0, data.totals.visitsAdults - data.aggregateVisitsAdults);
    this.nominalVisitsMinors =
      Math.max(0, data.totals.visitsMinors - data.aggregateVisitsMinors);
    this.form = this.fb.nonNullable.group({
      visitsAdults: [data.totals.visitsAdults, [Validators.required, Validators.min(this.nominalVisitsAdults)]],
      visitsMinors: [data.totals.visitsMinors, [Validators.required, Validators.min(this.nominalVisitsMinors)]],
      newAdults: [data.totals.newAdults, [Validators.required, Validators.min(0)]],
      newMinors: [data.totals.newMinors, [Validators.required, Validators.min(0)]]
    });

    if (data.readonly) {
      this.form.disable({ emitEvent: false });
    }
  }

  get visitsAdults(): number {
    return this.normalizedValue('visitsAdults');
  }

  get visitsMinors(): number {
    return this.normalizedValue('visitsMinors');
  }

  get newAdults(): number {
    return this.normalizedValue('newAdults');
  }

  get newMinors(): number {
    return this.normalizedValue('newMinors');
  }

  get totalAdults(): number {
    return this.data.totals.sealedAdults
      + this.data.totals.unsealedAdults
      + this.visitsAdults
      + this.newAdults;
  }

  get totalMinors(): number {
    return this.data.totals.sealedMinors
      + this.data.totals.unsealedMinors
      + this.visitsMinors
      + this.newMinors;
  }

  get grandTotal(): number {
    return this.totalAdults + this.totalMinors;
  }

  save(): void {
    if (this.form.invalid || this.data.readonly) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      visitsAdults: Math.max(0, this.visitsAdults - this.nominalVisitsAdults),
      visitsMinors: Math.max(0, this.visitsMinors - this.nominalVisitsMinors),
      newAdults: this.newAdults,
      newMinors: this.newMinors
    });
  }

  private normalizedValue(
    field: 'visitsAdults' | 'visitsMinors' | 'newAdults' | 'newMinors'
  ): number {
    const value = Number(this.form.controls[field].value);
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  }
}
